import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import '@/styles/SipaStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * SipaConfigPage Component
 * 
 * Provides SIPA form management functionality including:
 * - Export SIPA form structure to JSON
 * - Import SIPA form structure from JSON
 * - Generate SQL script for manual database migration
 */
const SipaConfigPage = () => {
    const { apiFetch } = useAuth();
    const fileInputRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [isGeneratingSql, setIsGeneratingSql] = useState(false);
    const [importPreview, setImportPreview] = useState(null);
    const [showImportConfirm, setShowImportConfirm] = useState(false);
    const [sqlScript, setSqlScript] = useState(null);
    const [showSqlModal, setShowSqlModal] = useState(false);

    /**
     * Export SIPA form structure to JSON file
     */
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const response = await apiFetch('/api/DataMigration/export-sipa-form');
            const result = await response.json();
            if (result.success) {
                const exportData = result.data;
                const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `sipa-form-export-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success(result.message || 'SIPA form exported successfully');
            } else {
                toast.error(result.message || 'Failed to export SIPA form');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Export error:', error);
            toast.error(error.message || 'Failed to export SIPA form');
        } finally {
            setIsExporting(false);
        }
    };

    /**
     * Confirm and execute import
     */
    const handleImportConfirm = async () => {
        if (!importPreview?.data) return;

        setIsImporting(true);
        try {
            const response = await apiFetch('/api/DataMigration/import-sipa-form?clearExisting=true', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(importPreview.data)
            });
            const result = await response.json();
            if (result.success) {
                toast.success(result.message || 'SIPA form imported successfully');
                setShowImportConfirm(false);
                setImportPreview(null);
            } else {
                toast.error(result.message || 'Failed to import SIPA form');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Import error:', error);
            toast.error(error.message || 'Failed to import SIPA form');
        } finally {
            setIsImporting(false);
        }
    };

    /**
     * Generate SQL script for manual migration
     */
    const handleGenerateSql = async () => {
        setIsGeneratingSql(true);
        try {
            const response = await apiFetch('/api/DataMigration/generate-sipa-sql');
            const result = await response.json();
            if (result.success) {
                const script = result.data;
                setSqlScript(script);
                setShowSqlModal(true);
                toast.success(result.message || 'SQL script generated successfully');
            } else {
                toast.error(result.message || 'Failed to generate SQL script');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('SQL generation error:', error);
            toast.error(error.message || 'Failed to generate SQL script');
        } finally {
            setIsGeneratingSql(false);
        }
    };

    /**
     * Download the SQL script as a file
     */
    const handleDownloadSql = () => {
        if (!sqlScript) return;
        const blob = new Blob([sqlScript], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sipa-form-migration-${new Date().toISOString().split('T')[0]}.sql`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    /**
     * Copy SQL script to clipboard
     */
    const handleCopySql = async () => {
        if (!sqlScript) return;
        try {
            await navigator.clipboard.writeText(sqlScript);
            toast.success('SQL script copied to clipboard');
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to copy to clipboard');
        }
    };

    return (
        <div className="sipa-config-page">
            <h1>SIPA Import and Export</h1>
            <p className="page-description">
                Manage the SIPA (Safety Inspection Property Assessment) form structure. 
                Export and import form configurations between environments.
            </p>

            {/* Export Section */}
            <div className="config-section">
                <h2>📤 Export Form Structure</h2>
                <p>Export the current SIPA form configuration to a JSON file. This includes all sections, subsections, controls, and options.</p>
                <div className="config-actions">
                    <button 
                        className="btn btn-secondary"
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        {isExporting ? 'Exporting...' : 'Export to JSON'}
                    </button>
                    <button 
                        className="btn btn-outline"
                        onClick={handleGenerateSql}
                        disabled={isGeneratingSql}
                    >
                        {isGeneratingSql ? 'Generating...' : 'Generate SQL Script'}
                    </button>
                </div>
            </div>

            {/* Import Section */}
            <div className="config-section">
                <h2>📥 Import Form Structure</h2>
                <p>
                    Import a SIPA form configuration from a JSON file. 
                    <strong className="warning-text"> Warning: This will replace all existing SIPA form data and DELETE all existing report answers!</strong>
                </p>
                <div className="config-actions">
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept=".json"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;

                            const reader = new FileReader();
                            reader.onload = (e) => {
                                try {
                                    const data = JSON.parse(e.target.result);
                                    // Validate structure
                                    if (!data.sections || !Array.isArray(data.sections)) {
                                        toast.error('Invalid SIPA form export file. Missing sections array.');
                                        return;
                                    }
                
                                    // Calculate totals for preview
                                    const totalSubSections = data.sections.reduce((sum, s) => sum + (s.subSections?.length || 0), 0);
                                    const totalControls = data.sections.reduce((sum, s) => 
                                        sum + (s.subSections?.reduce((ss, sub) => ss + (sub.controls?.length || 0), 0) || 0), 0);
                                    const totalOptions = data.sections.reduce((sum, s) => 
                                        sum + (s.subSections?.reduce((ss, sub) => 
                                            ss + (sub.controls?.reduce((cs, c) => cs + (c.options?.length || 0), 0) || 0), 0) || 0), 0);

                                    setImportPreview({
                                        data,
                                        fileName: file.name,
                                        exportDate: data.exportDate,
                                        sourceEnvironment: data.sourceEnvironment,
                                        sections: data.sections.length,
                                        subSections: totalSubSections,
                                        controls: totalControls,
                                        options: totalOptions
                                    });
                                    setShowImportConfirm(true);
                                } catch (parseError) {
                                    Sentry.captureException(parseError);
                                    console.error('Parse error:', parseError);
                                    toast.error('Failed to parse SIPA form export file. Invalid JSON format.');
                                }
                            };
                            reader.readAsText(file);
                            // Reset file input
                            event.target.value = '';
                        }}
                        style={{ display: 'none' }}
                    />
                    <button 
                        className="btn btn-warning"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isImporting}
                    >
                        {isImporting ? 'Importing...' : 'Import from JSON'}
                    </button>
                </div>
            </div>

            {/* Import Confirmation Modal */}
            {showImportConfirm && importPreview && (
                <div className="modal-overlay">
                    <div className="modal-content import-confirm-modal">
                        <h2>⚠️ Confirm Import</h2>
                        <p className="warning-text">
                            This will <strong>DELETE</strong> all existing SIPA form data <strong>AND all existing report answers</strong>, 
                            then replace the form structure with the imported configuration.
                        </p>
                        
                        <div className="import-preview">
                            <h3>Import Preview</h3>
                            <table className="preview-table">
                                <tbody>
                                    <tr>
                                        <td>File:</td>
                                        <td>{importPreview.fileName}</td>
                                    </tr>
                                    <tr>
                                        <td>Source Environment:</td>
                                        <td>{importPreview.sourceEnvironment || 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td>Export Date:</td>
                                        <td>{importPreview.exportDate ? new Date(importPreview.exportDate).toLocaleString() : 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td>Sections:</td>
                                        <td>{importPreview.sections}</td>
                                    </tr>
                                    <tr>
                                        <td>SubSections:</td>
                                        <td>{importPreview.subSections}</td>
                                    </tr>
                                    <tr>
                                        <td>Controls:</td>
                                        <td>{importPreview.controls}</td>
                                    </tr>
                                    <tr>
                                        <td>Options:</td>
                                        <td>{importPreview.options}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-actions">
                            <button 
                                className="btn btn-outline"
                                onClick={() => {
                                    setShowImportConfirm(false);
                                    setImportPreview(null);
                                }}
                                disabled={isImporting}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger"
                                onClick={handleImportConfirm}
                                disabled={isImporting}
                            >
                                {isImporting ? 'Importing...' : 'Confirm Import'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SQL Script Modal */}
            {showSqlModal && sqlScript && (
                <div className="modal-overlay">
                    <div className="modal-content sql-modal" style={{ maxWidth: '900px', maxHeight: '90vh' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2>📄 Generated SQL Script</h2>
                            <button 
                                className="btn btn-outline"
                                onClick={() => setShowSqlModal(false)}
                                style={{ minWidth: 'auto', padding: '8px 16px' }}
                            >
                                ✕
                            </button>
                        </div>
                        <p style={{ marginBottom: '16px', color: '#666' }}>
                            This SQL script can be used to manually migrate the SIPA form structure to another database.
                        </p>
                        <div style={{ 
                            background: '#1e1e1e', 
                            borderRadius: '8px', 
                            padding: '16px', 
                            maxHeight: '50vh', 
                            overflow: 'auto',
                            marginBottom: '16px'
                        }}>
                            <pre style={{ 
                                margin: 0, 
                                color: '#d4d4d4', 
                                fontSize: '0.85rem', 
                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                {sqlScript}
                            </pre>
                        </div>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button 
                                className="btn btn-outline"
                                onClick={handleCopySql}
                            >
                                📋 Copy to Clipboard
                            </button>
                            <button 
                                className="btn btn-secondary"
                                onClick={handleDownloadSql}
                            >
                                💾 Download .sql File
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SipaConfigPage;
